/**
 * Lógica PURA do "loop de retenção" — prova de que o toque do PT trouxe aluno de volta.
 *
 * Separada do hook (igual lib/retention.ts) pra ser testável sem Supabase/rede.
 * Regra HONESTA de "recuperado pelo toque":
 *   - estava SEM treinar nos 7 dias ANTES do 1º toque na janela, E
 *   - treinou DEPOIS do toque.
 * Assim não infla o número com quem já estava treinando quando recebeu a mensagem.
 */

export const DAY_MS = 86_400_000;
export const PRE_WINDOW_DAYS = 7; // "estava sumido" = sem treino nesses dias antes do toque

export interface RecoveryProof {
  janelaDias: number;
  notificados: number;
  voltaram: number;
  taxa: number; // % (voltaram / notificados)
}

export interface LogRow {
  student_ids: string[] | null;
  sent_at: string; // ISO
}

export interface SessionRow {
  student_id: string;
  completed_at: string; // ISO
}

/** aluno -> 1º toque (ms), considerando só logs com sent_at >= cutoffMs. */
export function buildFirstNudge(logs: LogRow[], cutoffMs: number): Record<string, number> {
  const map: Record<string, number> = {};
  for (const row of logs) {
    const t = new Date(row.sent_at).getTime();
    if (t < cutoffMs) continue;
    for (const id of row.student_ids ?? []) {
      if (map[id] === undefined || t < map[id]) map[id] = t;
    }
  }
  return map;
}

/**
 * Dado os logs de notificação e as sessões de treino, calcula a prova de recuperação.
 * `nowMs` injetável pra teste determinístico.
 */
export function computeRecoveryProof(
  logs: LogRow[],
  sessions: SessionRow[],
  janelaDias: number,
  nowMs: number
): RecoveryProof {
  const cutoff = nowMs - janelaDias * DAY_MS;
  const firstNudge = buildFirstNudge(logs, cutoff);
  const notifiedIds = Object.keys(firstNudge);
  const base: RecoveryProof = { janelaDias, notificados: notifiedIds.length, voltaram: 0, taxa: 0 };
  if (notifiedIds.length === 0) return base;

  const byStudent: Record<string, number[]> = {};
  for (const s of sessions) {
    (byStudent[s.student_id] ||= []).push(new Date(s.completed_at).getTime());
  }

  let voltaram = 0;
  for (const id of notifiedIds) {
    const nudge = firstNudge[id];
    const ts = byStudent[id] ?? [];
    const treinouAntes = ts.some((t) => t >= nudge - PRE_WINDOW_DAYS * DAY_MS && t < nudge);
    const treinouDepois = ts.some((t) => t > nudge);
    if (!treinouAntes && treinouDepois) voltaram++;
  }

  const notificados = notifiedIds.length;
  const taxa = notificados > 0 ? Math.round((voltaram / notificados) * 100) : 0;
  return { janelaDias, notificados, voltaram, taxa };
}
